import axios from 'axios';
import moment from 'moment';
import { xml2js } from 'xml-js';
import { logout } from '../store/actions/Authentication';
import { addFlashMessage, clearMessage } from '../store/actions/Flash';

const { REACT_APP_API_URL } = process.env;

export const endpoints = {
  login: '/api/v1/auth_user',
  forgotPassword: '/api/v1/password/forgot',
  changePassword: '/api/v1/me/reset_password',
  resetPasswordToken: '/api/v1/password/reset_token',
  resetPassword: '/api/v1/password/reset',
  createAccount: '/api/v1/users',
  me: '/api/v1/me',
  projects: '/api/v1/projects',
  s3Signature: '/api/v1/s3-signature',
  deleteFile: '/api/v1/s3',
  refreshToken: '/api/v1/refresh_token',
  countries: '/api/v1/countries',
  states: '/api/v1/states',
  basicSchedule: '/api/v1/me/basic_schedules',
  advancedSchedule: '/api/v1/me/advanced_schedules',
  freelancers: '/api/v1/freelancers',
  notifications: '/api/v1/notifications',
  instantBook: '/api/v1/instant_book_items',
  references: '/api/v1/references',
  confirmEmail: '/api/v1/users/confirmation',
  resendConfirmation: '/api/v1/users/resend_confirmation',
  managers: '/api/v1/managers',
  tracks: '/api/v1/tracks',
};

export const axiosInstance = axios.create({
  baseURL: REACT_APP_API_URL,
  timeout: 30000,
});

export const tokenKeys = {
  tokenKey: 'token',
  tokenRefreshKey: 'token_refresh',
  tokenCreatedAtKey: 'token_created_at',
  tokenExpiresInKey: 'token_expires_in',
};

export const saveToken = json => {
  const {
    tokenKey,
    tokenRefreshKey,
    tokenCreatedAtKey,
    tokenExpiresInKey,
  } = tokenKeys;
  sessionStorage.setItem(tokenKey, json.token);
  sessionStorage.setItem(tokenRefreshKey, json.refresh_token);
  sessionStorage.setItem(tokenCreatedAtKey, json.created_at);
  sessionStorage.setItem(tokenExpiresInKey, json.expires_in);
};

export const removeToken = () => {
  const {
    tokenKey,
    tokenRefreshKey,
    tokenCreatedAtKey,
    tokenExpiresInKey,
  } = tokenKeys;
  sessionStorage.removeItem(tokenKey);
  sessionStorage.removeItem(tokenRefreshKey);
  sessionStorage.removeItem(tokenCreatedAtKey);
  sessionStorage.removeItem(tokenExpiresInKey);
};

export const isTokenExpired = () => {
  const { tokenCreatedAtKey, tokenExpiresInKey } = tokenKeys;
  const createdAt = sessionStorage.getItem(tokenCreatedAtKey);
  const expiresIn = sessionStorage.getItem(tokenExpiresInKey);
  const timeLimit = moment(createdAt).add(expiresIn, 'seconds');
  const timeNow = moment();
  return timeNow.isAfter(timeLimit);
};

export const addTokenToHeader = (config, token) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: `jwt ${token}`,
  },
});

const showErrorMessage = (dispatch, message) => {
  dispatch(clearMessage());
  dispatch(addFlashMessage([message], 'error'));
};

const logoutUser = dispatch => {
  dispatch(logout());
  setTimeout(() => showErrorMessage(dispatch, 'Session expired.'), 200);
};

export const setupInterceptors = ({ dispatch }) => {
  axiosInstance.interceptors.request.use(
    config => {
      const { tokenKey, tokenRefreshKey } = tokenKeys;
      const token = sessionStorage.getItem(tokenKey);

      if (!token || config.url === endpoints.refreshToken) return config;
      if (!isTokenExpired()) return addTokenToHeader(config, token);

      const refreshToken = sessionStorage.getItem(tokenRefreshKey);
      return api
        .refreshToken(refreshToken)
        .then(response => response.data)
        .then(json => {
          saveToken(json);
          return Promise.resolve(addTokenToHeader(config, json.token));
        })
        .catch(error => {
          if (error.response.status !== 401) return Promise.resolve(config);
          logoutUser(dispatch);
          return Promise.resolve(config);
        });
    },
    error => Promise.reject(error),
  );
};

const api = {
  login: data => axiosInstance.post(endpoints.login, data),
  forgotPassword: data => axiosInstance.post(endpoints.forgotPassword, data),
  changePassword: data => axiosInstance.put(endpoints.changePassword, data),
  resetPasswordToken: data => axiosInstance.post(endpoints.resetPasswordToken, data),
  resetPassword: data => axiosInstance.post(endpoints.resetPassword, data),
  createAccount: data =>
    axiosInstance.post(endpoints.createAccount, { user: data }),
  fetchUser: () => axiosInstance.get(endpoints.me),
  updateUser: data => axiosInstance.put(endpoints.me, { user: data }),
  fetchProjects: () => axiosInstance.get(endpoints.projects),
  createProject: data => axiosInstance.post(endpoints.projects, data),
  saveProject: (data, id) =>
    axiosInstance.put(`${endpoints.projects}/${id}`, data),
  discardProject: id =>
    axiosInstance.put(`${endpoints.projects}/${id}/discard`),
  getS3signature: () => axiosInstance.get(endpoints.s3Signature),
  deleteFile: url => axiosInstance.delete(`${endpoints.deleteFile}?url=${url}`),
  refreshToken: refreshToken =>
    axiosInstance.post(endpoints.refreshToken, { refresh_token: refreshToken }),
  fetchCountries: query =>
    axiosInstance.get(
      `${endpoints.countries}?q[name_start]=${query}&q[s]=name%20asc`,
    ),
  fetchDialCode: query =>
    axiosInstance.get(
      `${
        endpoints.countries
      }?q[dial_code_not_eq]=null&q[dial_code_start]=%2B${query}&q[s]=dial_code%20asc`,
    ),
  fetchStates: (query, country) =>
    axiosInstance.get(
      `${
        endpoints.states
      }?q[name_start]=${query}&q[country_name_eq]=${country}&q[s]=name%20asc`,
    ),
  fetchUSAStates: () => axiosInstance.get(endpoints.states),
  saveDayAvailability: day =>
    axiosInstance.post(`${endpoints.basicSchedule}`, day),
  updateDayAvailability: day =>
    axiosInstance.put(`${endpoints.basicSchedule}/${day.id}`, day),
  getWeekAvailability: () => axiosInstance.get(`${endpoints.basicSchedule}`),
  saveAdvancedSchedule: day =>
    axiosInstance.post(`${endpoints.advancedSchedule}`, day),
  fetchAdvancedSchedule: () =>
    axiosInstance.get(`${endpoints.advancedSchedule}`),
  updateAdvancedSchedule: day =>
    axiosInstance.put(`${endpoints.advancedSchedule}/${day.id}`, day),
  getVimeoThumbnail: vimeoId =>
    axios
      .get(`https://vimeo.com/api/v2/video/${vimeoId}.xml`)
      .then(({ data }) => {
        const {
          videos: {
            video: {
              thumbnail_large: { _text: thumbnailUrl },
            },
          },
        } = xml2js(data, { compact: true });
        return thumbnailUrl;
      }),
  getYoutubeThumbnail: youtubeId => {
    const thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
    return Promise.resolve(thumbnailUrl);
  },
  fetchFreelancers: params =>
    axiosInstance.get(endpoints.freelancers, { params }),
  getBudget: projectId =>
    axiosInstance.get(`${endpoints.projects}/${projectId}/budget`),
  requestBid: (projectId, freelancerId) =>
    axiosInstance.post(`${endpoints.projects}/${projectId}/bids`, {
      bid: { freelancer_id: freelancerId },
    }),
  deleteBid: (projectId, bidId) =>
    axiosInstance.delete(`${endpoints.projects}/${projectId}/bids/${bidId}`),
  getProjectFreelancers: projectId =>
    axiosInstance.get(`${endpoints.projects}/${projectId}/freelancers`),
  updateBid: ({ projectId, bidId, ...rest }) =>
    axiosInstance.put(`${endpoints.projects}/${projectId}/bids/${bidId}`, rest),
  acceptBid: ({ projectId, ...rest }) =>
    axiosInstance.put(`${endpoints.projects}/${projectId}/accept-bid`, rest),
  bookNow: (projectId, freelancerId) =>
    axiosInstance.put(`${endpoints.projects}/${projectId}`, {
      freelancer_id: freelancerId,
    }),
  getProject: id => axiosInstance.get(`${endpoints.projects}/${id}`),
  sendPaymentToken: ({ projectId, token }) =>
    axiosInstance.post(`${endpoints.projects}/${projectId}/charge`, {
      stripe_token: token.id,
    }),
  checkNewNotifications: () =>
    axiosInstance.get(`${endpoints.notifications}/check`),
  getNotifications: params =>
    axiosInstance.get(endpoints.notifications, { params }),
  updateNotification: (id, notification) =>
    axiosInstance.put(`${endpoints.notifications}/${id}`, notification),
  markAllNotificationsAsRead: () =>
    axiosInstance.post(`${endpoints.notifications}/mark-as-read`),
  getInstantBookInfo: () => axiosInstance.get(endpoints.instantBook),
  updateInstantBookInfo: ({ id, ...rest }) =>
    axiosInstance.put(`${endpoints.instantBook}/${id}`, rest),
  deleteReference: id => axiosInstance.delete(`${endpoints.references}/${id}`),
  confirmEmail: token => axiosInstance.post(endpoints.confirmEmail, token),
  resendConfirmation: email =>
    axiosInstance.post(endpoints.resendConfirmation, email),
  getManagers: () => axiosInstance.get(endpoints.managers),
  getFreelancers: () => axiosInstance.get(`${endpoints.freelancers}/all`),
  getTrack: () => axiosInstance.post(endpoints.tracks),
  updateTrack: data =>
    axiosInstance.put(`${endpoints.tracks}/${data.track.id}`, data),
};

export default api;



// WEBPACK FOOTER //
// ./src/api/index.js